import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useArticles } from '../hooks/useArticles';
import { useToast } from '../hooks/useToast';
import { useOnline } from '../hooks/useOnline';
import {
  shouldShowLanguageBadge,
  getLanguageName,
  getLanguageBadgeClasses,
  formatLanguageCode
} from '../utils/language';
import { formatRelativeDate } from '../utils/dateUtils';

// Color accent options for visual variety based on domain
const ACCENT_COLORS = [
  { border: 'border-l-fun-mint', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  { border: 'border-l-fun-coral', bg: 'bg-rose-50 dark:bg-rose-950/30' },
  { border: 'border-l-fun-sky', bg: 'bg-sky-50 dark:bg-sky-950/30' },
  { border: 'border-l-fun-lemon', bg: 'bg-amber-50 dark:bg-amber-950/30' },
  { border: 'border-l-fun-pink', bg: 'bg-pink-50 dark:bg-pink-950/30' },
  { border: 'border-l-primary-500', bg: 'bg-indigo-50 dark:bg-indigo-950/30' },
  { border: 'border-l-secondary-500', bg: 'bg-purple-50 dark:bg-purple-950/30' },
  { border: 'border-l-accent-500', bg: 'bg-orange-50 dark:bg-orange-950/30' },
];

// Simple hash function to get consistent color for a domain
const getDomainColorIndex = (domain) => {
  if (!domain) return 0;
  let hash = 0;
  for (let i = 0; i < domain.length; i++) {
    hash = ((hash << 5) - hash) + domain.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash) % ACCENT_COLORS.length;
};

const ArticleCard = ({ article, onDelete, className = '', style = {} }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCelebrating, setIsCelebrating] = useState(false);
  const { removeArticle, markAsRead, markAsUnread, isDeleting, isUpdating } = useArticles();
  const { showSuccess, showError } = useToast();
  const online = useOnline();


  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return 'Unknown source';
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Strip markdown bold markers for cleaner display on dashboard
  const stripMarkdownBold = (text) => {
    if (!text) return text;
    // Remove ** bold markers for cleaner display
    return text.replace(/\*\*/g, '');
  };

  const handleDelete = async () => {
    try {
      await removeArticle(article.id);
      if (onDelete) {
        onDelete(article.id);
      }
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteConfirm(false);
  };

  const handleMarkAsRead = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await markAsRead(article.id);
      // Trigger celebration animation
      setIsCelebrating(true);
      setTimeout(() => setIsCelebrating(false), 600);
      showSuccess('Nice! Article marked as read!');
    } catch (error) {
      console.error('Error marking article as read:', error);
      showError('Failed to mark article as read');
    }
  };

  const handleMarkAsUnread = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await markAsUnread(article.id);
      showSuccess('Article marked as unread!');
    } catch (error) {
      console.error('Error marking article as unread:', error);
      showError('Failed to mark article as unread');
    }
  };

  const readingTime = article.content ?
    Math.ceil(article.content.split(' ').length / 200) : 0;

  const domain = article.domain || getDomainFromUrl(article.url);
  const colorIndex = getDomainColorIndex(domain);
  const accentColor = ACCENT_COLORS[colorIndex];

  return (
    <article
      className={`group relative bg-surface rounded-clay-lg border-3 border-clay-border
        shadow-clay overflow-hidden
        transition-all duration-300 ease-smooth-out
        ${article.is_read
          ? 'opacity-70 bg-surface-alt/50'
          : `hover:shadow-clay-hover hover:-translate-y-1 ${accentColor.border} border-l-4`
        }
        ${isCelebrating ? 'animate-celebrate' : ''}
        ${className}`}
      style={style}
    >

      <Link to={`/article/${article.id}`} className="block">
        <div className="p-3 sm:p-5">
          {/* Header with domain and date */}
          <div className="flex items-center justify-between text-xs text-secondary-text mb-3">
            <div className="flex items-center flex-wrap gap-2">
              <span className="badge-fun-primary text-xs py-0.5">
                {domain}
              </span>
              {/* Read status indicator */}
              {article.is_read && (
                <span className="badge-fun-mint text-xs py-0.5 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Done
                </span>
              )}
              {/* Language indicator */}
              {shouldShowLanguageBadge(article.language) && (
                <span
                  className={getLanguageBadgeClasses(article.language)}
                  title={`Article language: ${getLanguageName(article.language)}`}
                >
                  {formatLanguageCode(article.language)}
                </span>
              )}
              {readingTime > 0 && (
                <span className="text-tertiary-text">{readingTime} min</span>
              )}
            </div>
            <time dateTime={article.publish_date || article.created_at} className="text-tertiary-text">
              {formatRelativeDate(article.publish_date || article.created_at)}
            </time>
          </div>

          {/* Title */}
          <h3 className={`font-display text-lg font-semibold mb-3 line-clamp-2
            group-hover:text-primary-600 transition-colors duration-200 ${
            article.is_read ? 'text-secondary-text' : 'text-heading'
          }`}>
            {article.title || 'Untitled Article'}
          </h3>

          {/* Summary or excerpt */}
          {article.summary && (
            <p className={`text-sm mb-4 line-clamp-3 leading-relaxed ${
              article.is_read ? 'text-tertiary-text' : 'text-secondary-text'
            }`}>
              {truncateText(stripMarkdownBold(article.summary), 150)}
            </p>
          )}

          {/* Footer with status and actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {/* Processing status */}
              {article.processing_status === 'pending' && (
                <div className="flex items-center space-x-1.5 text-xs text-primary-600">
                  <div className="spinner-fun-sm w-3 h-3"></div>
                  <span className="font-medium">Queued...</span>
                </div>
              )}

              {article.processing_status === 'processing' && (
                <div className="flex items-center space-x-1.5 text-xs text-accent-600">
                  <div className="spinner-fun-sm w-3 h-3"></div>
                  <span className="font-medium">Processing...</span>
                </div>
              )}

              {article.processing_status === 'failed' && (
                <div className="flex items-center space-x-1.5 text-xs text-fun-coral">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Oops! Failed</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center space-x-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200">
              {/* Done/Undone button */}
              <button
                onClick={article.is_read ? handleMarkAsUnread : handleMarkAsRead}
                disabled={isUpdating}
                className={`p-2 rounded-clay transition-all duration-200 ${
                  article.is_read
                    ? 'text-accent-600 hover:text-accent-700 hover:bg-accent-50 hover:shadow-clay-sm'
                    : 'text-fun-mint hover:bg-emerald-50 hover:shadow-clay-sm'
                }`}
                title={article.is_read ? "Mark as unread" : "Mark as done"}
                aria-label={article.is_read ? "Mark as unread" : "Mark as done"}
              >
                {isUpdating ? (
                  <div className="spinner-fun-sm w-4 h-4"></div>
                ) : article.is_read ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>

              {/* Delete button */}
              <button
                onClick={handleDeleteClick}
                disabled={isDeleting || !online}
                className="p-2 text-tertiary-text hover:text-fun-coral hover:bg-rose-50 rounded-clay transition-all duration-200 hover:shadow-clay-sm"
                title={!online ? "Connect to internet to do this" : "Delete article"}
                aria-label="Delete article"
              >
                {isDeleting ? (
                  <div className="spinner-fun-sm w-4 h-4"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </Link>

      {/* Delete confirmation modal - Claymorphism style */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-surface/95 backdrop-blur-sm flex items-center justify-center z-10 rounded-clay-lg">
          <div className="text-center p-6">
            <div className="text-4xl mb-3">
              <span role="img" aria-label="thinking">🤔</span>
            </div>
            <div className="text-sm font-display font-semibold text-heading mb-4">
              Delete this article?
            </div>
            <div className="flex justify-center space-x-3">
              <button
                onClick={handleCancelDelete}
                className="btn-clay-ghost btn-clay-sm"
              >
                Keep it
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting || !online}
                className="btn-clay-danger btn-clay-sm"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default ArticleCard;
